import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Input,
  Label,
} from "reactstrap";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import {
  FaUsers,
  FaGift,
  FaFileExcel,
  FaPlus,
  FaSearch,
  FaChartPie,
  FaDownload,
  FaFilter,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import {
  ApiGetAllUsuariosNavidad,
  ApiGetTotalUsuariosNavidad,
} from "../../../api/navidad";
import UsuariosNavidadTable from "../../../components/admin/navidad/UsuariosNavidadTable";
import ExcelUploadModal from "../../../components/admin/navidad/ExcelUploadModal";
import UsuarioNavidadForm from "../../../components/admin/navidad/UsuarioNavidadForm";
import ConfiguracionesModal from "../../../components/admin/navidad/ConfiguracionesModal";

const GestionNavidad = () => {
  const [allUsuarios, setAllUsuarios] = useState([]);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [loading, setLoading] = useState(true);
  const [updateList, setUpdateList] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);

  const [filtroEntrega, setFiltroEntrega] = useState("todos");

  const [filtroCodigo, setFiltroCodigo] = useState("todos");

  // Filtros específicos de búsqueda
  const [filtroCodigoBusqueda, setFiltroCodigoBusqueda] = useState("");
  const [filtroCedula, setFiltroCedula] = useState("");
  const [filtroNombres, setFiltroNombres] = useState("");

  // Estados para modales
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showConfiguraciones, setShowConfiguraciones] = useState(false);

  // Estados para formulario
  const [selectedUser, setSelectedUser] = useState(null);
  const [formType, setFormType] = useState("create");

  useEffect(() => {
    loadData();
  }, [updateList]);

  const loadData = async () => {
    setLoading(true);
    try {
      const commonParams = {
        estado: true,
      };

      const totalResult = await ApiGetTotalUsuariosNavidad(commonParams);
      let totalUsuarios = 0;

      if (totalResult.sms === "ok") {
        totalUsuarios = totalResult.data;
        setTotalUsuarios(totalUsuarios);
      }

      let allUsuariosData = [];
      const pageSize = 200; // Tamaño de página para la carga
      const totalPages = Math.ceil(totalUsuarios / pageSize);

      const usuariosMap = new Map();

      for (let i = 1; i <= totalPages; i++) {
        const usuariosResult = await ApiGetAllUsuariosNavidad({
          ...commonParams,
          page: i,
          pageSize: pageSize,
        });

        if (usuariosResult.sms === "ok" && Array.isArray(usuariosResult.data)) {
          usuariosResult.data.forEach((usuario) => {
            if (usuario.pre_usuario_navidad_id) {
              usuariosMap.set(usuario.pre_usuario_navidad_id, usuario);
            }
          });
        }
      }

      allUsuariosData = Array.from(usuariosMap.values());

      setAllUsuarios(allUsuariosData);
    } catch (error) {
      toast.error("Error al cargar los datos");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateList = () => {
    setUpdateList(!updateList);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setFormType("create");
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormType("edit");
    setShowUserForm(true);
  };

  const handleCloseUserForm = () => {
    setShowUserForm(false);
    setSelectedUser(null);
  };

  const handleCloseExcelModal = () => {
    setShowExcelModal(false);
  };

  const filteredUsuarios = allUsuarios.filter((user) => {
    if (filtroEntrega === "entregadas" && !user.recibio_canasta) return false;
    if (filtroEntrega === "pendientes" && user.recibio_canasta) return false;

    if (filtroCodigo !== "todos") {
      const tieneCodigo =
        user.code != null && user.code !== ""
          ? typeof user.code === "string"
            ? user.code.trim().length > 0
            : Boolean(user.code)
          : false;

      if (filtroCodigo === "con" && !tieneCodigo) return false;
      if (filtroCodigo === "sin" && tieneCodigo) return false;
    }

    const safeIncludes = (v, searchValue) => {
      if (!searchValue || !searchValue.trim()) return true;
      if (v == null || v === "") return false;
      const searchLower = searchValue.toLowerCase().trim();
      if (typeof v === "string") {
        return v.toLowerCase().includes(searchLower);
      }
      return String(v).toLowerCase().includes(searchLower);
    };

    if (filtroCodigoBusqueda && filtroCodigoBusqueda.trim()) {
      if (!safeIncludes(user.code, filtroCodigoBusqueda)) return false;
    }

    if (filtroCedula && filtroCedula.trim()) {
      if (!safeIncludes(user.usuario, filtroCedula)) return false;
    }

    if (filtroNombres && filtroNombres.trim()) {
      if (!safeIncludes(user.nombres, filtroNombres)) return false;
    }

    return true;
  });
  
  const totalFilteredUsuarios = filteredUsuarios.length;
  useEffect(() => {
    const totalPages = Math.ceil(totalFilteredUsuarios / pageSize);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalFilteredUsuarios, currentPage, pageSize]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsuarios = filteredUsuarios.slice(startIndex, endIndex);

  const resetFilters = () => {
    setFiltroEntrega("todos");
    setFiltroCodigo("todos");
    setFiltroCodigoBusqueda("");
    setFiltroCedula("");
    setFiltroNombres("");
    setCurrentPage(1);
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        Usuario: "1234567890",
        Code: "EMP001",
        Apellidos: "GARCIA",
        Nombres: "JUAN CARLOS",
        Cargo: "GERENTE",
        Area: "ADMINISTRACION",
        Email: "juan.garcia@empresa.com",
      },
      {
        Usuario: "0987654321",
        Code: "EMP002",
        Apellidos: "LOPEZ",
        Nombres: "MARIA ELENA",
        Cargo: "OPERADOR",
        Area: "PRODUCCION",
        Email: "maria.lopez@empresa.com",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "plantilla_usuarios_navidad.xlsx");
    toast.success("Plantilla descargada correctamente");
  };

  const exportToExcel = () => {
    if (filteredUsuarios.length === 0) {
      toast.error("No hay datos para exportar");
      return;
    }

    const exportData = filteredUsuarios.map((user) => ({
      Usuario: user.usuario,
      Code: user.code,
      Apellidos: user.apellidos,
      Nombres: user.nombres,
      Cargo: user.cargo || "",
      Area: user.area || "",
      Email: user.correo || "",
      Telefono: user.telefono || "",
      Direccion: user.direccion || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Usuarios Navidad");
    XLSX.writeFile(
      wb,
      `usuarios_navidad_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success("Archivo exportado correctamente");
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <Card className="shadow-sm border-0 mb-4">
              <div className="card-header bg-gradient text-bg-info">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-0 d-flex align-items-center">
                      <FaGift className="me-2" />
                      Gestión de Usuarios Navidad
                    </h4>
                    <p className="mb-0 mt-1 opacity-75">
                      Administra los usuarios elegibles para canastas navideñas
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      color="outline-light"
                      onClick={exportToExcel}
                      className="d-flex align-items-center"
                    >
                      <FaDownload className="me-1" />
                      Exportar
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {/* Estadísticas rápidas */}
            <Row className="mb-4">
              <Col md={3}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <FaUsers size={32} className="text-primary mb-2" />
                      <h5 className="text-primary mb-1">{totalUsuarios}</h5>
                      <small className="text-muted">Total Usuarios</small>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col md={3}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <FaGift size={32} className="text-success mb-2" />
                      <h5 className="text-success mb-1">
                        {
                          allUsuarios.filter((user) => user.recibio_canasta)
                            .length
                        }
                      </h5>
                      <small className="text-muted">Canastas Entregadas</small>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col md={3}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <FaFileExcel size={32} className="text-info mb-2" />
                      <h5 className="text-info mb-1">
                        {totalFilteredUsuarios}
                      </h5>
                      <small className="text-muted">Filtrados</small>
                    </div>
                  </Card>
                </motion.div>
              </Col>
              <Col md={3}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="border-0 shadow-sm h-100">
                    <div className="card-body text-center">
                      <FaGift size={32} className="text-warning mb-2" />
                      <h5 className="text-warning mb-1">
                        {
                          allUsuarios.filter((user) => !user.recibio_canasta)
                            .length
                        }
                      </h5>
                      <small className="text-muted">Canastas Pendientes</small>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            </Row>

            {/* Controles */}
            <Card className="shadow-sm border-0 mb-4">
              <div className="card-body">
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        onClick={handleCreateUser}
                        className="d-flex align-items-center"
                      >
                        <FaPlus className="me-1" />
                        Nuevo Usuario
                      </Button>
                      <Button
                        color="success"
                        onClick={() => setShowExcelModal(true)}
                        className="d-flex align-items-center"
                      >
                        <FaFileExcel className="me-1" />
                        Cargar Excel
                      </Button>
                      <Button
                        color="outline-info"
                        onClick={downloadTemplate}
                        className="d-flex align-items-center"
                      >
                        <FaDownload className="me-1" />
                        Plantilla
                      </Button>
                      <Button
                        color="outline-warning"
                        onClick={() => setShowConfiguraciones(true)}
                        className="d-flex align-items-center"
                      >
                        <FaUsers className="me-1" />
                        Configuraciones
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>

            <Row className="my-3">
              {/* Filtro entrega */}
              <Col md={3}>
                <Label className="fw-bold mb-1">
                  <FaGift className="me-1" />
                  Estado de Canasta
                </Label>
                <Input
                  type="select"
                  value={filtroEntrega}
                  onChange={(e) => {
                    setFiltroEntrega(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="todos">Todas</option>
                  <option value="entregadas">Entregadas</option>
                  <option value="pendientes">Pendientes</option>
                </Input>
              </Col>

              {/* Filtro código (con/sin) */}
              <Col md={3}>
                <Label className="fw-bold mb-1">
                  <FaFilter className="me-1" />
                  Código
                </Label>
                <Input
                  type="select"
                  value={filtroCodigo}
                  onChange={(e) => {
                    setFiltroCodigo(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="todos">Todos</option>
                  <option value="con">Con código</option>
                  <option value="sin">Sin código</option>
                </Input>
              </Col>

              {/* Filtro búsqueda por código específico */}
              <Col md={2}>
                <Label className="fw-bold mb-1">
                  <FaSearch className="me-1" />
                  Buscar Código
                </Label>
                <Input
                  type="text"
                  placeholder="Código..."
                  value={filtroCodigoBusqueda}
                  onChange={(e) => {
                    setFiltroCodigoBusqueda(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </Col>

              {/* Filtro búsqueda por cédula */}
              <Col md={2}>
                <Label className="fw-bold mb-1">
                  <FaUser className="me-1" />
                  Buscar Cédula
                </Label>
                <Input
                  type="text"
                  placeholder="Cédula..."
                  value={filtroCedula}
                  onChange={(e) => {
                    setFiltroCedula(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </Col>

              {/* Filtro búsqueda por nombres */}
              <Col md={2}>
                <Label className="fw-bold mb-1">
                  <FaUser className="me-1" />
                  Buscar Nombres
                </Label>
                <Input
                  type="text"
                  placeholder="Nombres..."
                  value={filtroNombres}
                  onChange={(e) => {
                    setFiltroNombres(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </Col>
            </Row>

            {/* Botón limpiar filtros */}
            <Row className="mb-3">
              <Col>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={resetFilters}
                  className="d-flex align-items-center"
                >
                  <FaTimes className="me-1" />
                  Limpiar Filtros
                </Button>
              </Col>
            </Row>

            {/* Tabla de usuarios */}
            <Card className="shadow-sm border-0">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-muted">Cargando usuarios...</p>
                  </div>
                ) : (
                  <UsuariosNavidadTable
                    usuarios={paginatedUsuarios}
                    onEdit={handleEditUser}
                    onUpdateList={handleUpdateList}
                    loading={loading}
                    totalUsuarios={totalFilteredUsuarios}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                  />
                )}
              </div>
            </Card>

            {/* Modales */}
            <AnimatePresence>
              {showExcelModal && (
                <ExcelUploadModal
                  isOpen={showExcelModal}
                  onClose={handleCloseExcelModal}
                  onSuccess={handleUpdateList}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showUserForm && (
                <UsuarioNavidadForm
                  isOpen={showUserForm}
                  onClose={handleCloseUserForm}
                  onSuccess={handleUpdateList}
                  user={selectedUser}
                  type={formType}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showConfiguraciones && (
                <ConfiguracionesModal
                  isOpen={showConfiguraciones}
                  onClose={() => setShowConfiguraciones(false)}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </Col>
      </Row>
    </Container>
  );
};

export default GestionNavidad;
